import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Shield, Info, Loader2 } from "lucide-react";
import { useMentorContactVisibility, useUpdateMentorContactVisibility } from "@/hooks/useMentorContactVisibility";

interface MentorContactPrivacySectionProps {
  isEditMode?: boolean;
}

export function MentorContactPrivacySection({ isEditMode = false }: MentorContactPrivacySectionProps) {
  const { data: visibility, isLoading } = useMentorContactVisibility();
  const updateVisibility = useUpdateMentorContactVisibility();

  // Local state for form
  const [hidePhone, setHidePhone] = useState(false);
  const [hideEmail, setHideEmail] = useState(false);
  const [hideLinkedin, setHideLinkedin] = useState(false);

  // Track if there are unsaved changes
  const [hasChanges, setHasChanges] = useState(false);

  // Initialize form state when data loads
  useEffect(() => {
    if (visibility) {
      setHidePhone(visibility.hide_phone_from_all);
      setHideEmail(visibility.hide_email_from_connected);
      setHideLinkedin(visibility.hide_linkedin_from_connected);
    }
  }, [visibility]);

  // Track changes
  useEffect(() => {
    if (!visibility) {
      // If no record exists, any checked box is a change
      setHasChanges(hidePhone || hideEmail || hideLinkedin);
    } else {
      setHasChanges(
        hidePhone !== visibility.hide_phone_from_all ||
        hideEmail !== visibility.hide_email_from_connected ||
        hideLinkedin !== visibility.hide_linkedin_from_connected
      );
    }
  }, [hidePhone, hideEmail, hideLinkedin, visibility]);

  const handleSave = async () => {
    await updateVisibility.mutateAsync({
      hide_phone_from_all: hidePhone,
      hide_email_from_connected: hideEmail,
      hide_linkedin_from_connected: hideLinkedin,
    });
  };

  const handleReset = () => {
    if (visibility) {
      setHidePhone(visibility.hide_phone_from_all);
      setHideEmail(visibility.hide_email_from_connected);
      setHideLinkedin(visibility.hide_linkedin_from_connected);
    } else {
      setHidePhone(false);
      setHideEmail(false);
      setHideLinkedin(false);
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-6 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  // View mode - show current settings
  if (!isEditMode) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary" />
            Contact Privacy
          </CardTitle>
          <CardDescription>
            Your contact visibility preferences
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Checkbox checked={visibility?.hide_phone_from_all ?? false} disabled />
              <span className="text-sm">
                Phone number hidden from everyone
                {(visibility?.hide_phone_from_all ?? false) && <span className="text-muted-foreground ml-1">(only admins can see)</span>}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox checked={visibility?.hide_email_from_connected ?? false} disabled />
              <span className="text-sm">
                Email hidden from connected entrepreneurs
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox checked={visibility?.hide_linkedin_from_connected ?? false} disabled />
              <span className="text-sm">
                LinkedIn hidden from connected entrepreneurs
              </span>
            </div>
          </div>

          <Alert className="border-muted bg-muted/30">
            <Info className="h-4 w-4" />
            <AlertDescription className="text-xs">
              Admins always have access to your contact information for platform management purposes.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  // Edit mode - show form
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="h-5 w-5 text-primary" />
          Contact Privacy
        </CardTitle>
        <CardDescription>
          Control who can see your contact information
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <Checkbox
              id="hide-phone"
              checked={hidePhone}
              onCheckedChange={(checked) => setHidePhone(checked === true)}
            />
            <div className="grid gap-1.5 leading-none">
              <Label htmlFor="hide-phone" className="cursor-pointer">
                Hide phone number from everyone
              </Label>
              <p className="text-xs text-muted-foreground">
                Only admins will be able to see your phone number
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Checkbox
              id="hide-email"
              checked={hideEmail}
              onCheckedChange={(checked) => setHideEmail(checked === true)}
            />
            <div className="grid gap-1.5 leading-none">
              <Label htmlFor="hide-email" className="cursor-pointer">
                Hide email from connected entrepreneurs
              </Label>
              <p className="text-xs text-muted-foreground">
                Connected entrepreneurs won't see your email address
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Checkbox
              id="hide-linkedin"
              checked={hideLinkedin}
              onCheckedChange={(checked) => setHideLinkedin(checked === true)}
            />
            <div className="grid gap-1.5 leading-none">
              <Label htmlFor="hide-linkedin" className="cursor-pointer">
                Hide LinkedIn from connected entrepreneurs
              </Label>
              <p className="text-xs text-muted-foreground">
                Connected entrepreneurs won't see your LinkedIn profile
              </p>
            </div>
          </div>
        </div>

        <Alert className="border-muted bg-muted/30">
          <Info className="h-4 w-4" />
          <AlertDescription className="text-xs">
            Admins always have access to your contact information for platform management purposes.
          </AlertDescription>
        </Alert>

        <div className="flex justify-end gap-2 pt-2">
          {hasChanges && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleReset}
              disabled={updateVisibility.isPending}
            >
              Reset
            </Button>
          )}
          <Button
            size="sm"
            onClick={handleSave}
            disabled={!hasChanges || updateVisibility.isPending}
          >
            {updateVisibility.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save Privacy Settings
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
